import React, { useEffect, useMemo, useRef } from "react";
import { Grid } from "@material-ui/core";
import { RRule } from "rrule";
import { Column } from "components/Layout";
import { SmallText, Text } from "components/Text";
import { useFormikContext } from "formik";
import { Input } from "../Input";
import { SelectField } from "../Select";
import {
  FieldName,
  FREQUENCY_OPTIONS,
  MONTHLY_WEEKDAY_OPTIONS,
  Suffix,
  WEEKLY_WEEKDAY_OPTIONS,
} from "./constants";
import { getFieldName, getQuarterDayRule, parseWeekdayString } from "./utils";
import { RRuleConfigProps } from "./types";
import { withNamespace } from "../lib";

export const TradingTermInputs: React.FC<RRuleConfigProps> = ({
  namespace,
  termType,
  values,
  name,
  index,
  setFieldValue,
}) => {
  const fieldPrefix = withNamespace(namespace, termType);

  // Get the current row based on the name and index
  const currentRow = useMemo(
    () => values[name]?.[index] || {},
    [values, name, index],
  );
  // Extract the term configuration for the current term type
  const termConfig = useMemo(
    () => currentRow[getFieldName(termType, Suffix.CONFIG_SUFFIX)] || {},
    [currentRow, termType],
  );
  const { freq } = termConfig;
  const { errors } = useFormikContext();
  // Generate and set RRule when config changes
  useEffect(() => {
    if (termConfig.freq !== undefined) {
      // If the frequency is daily, we should not set a schedule rule
      // We can just use the terms
      if (termConfig.freq === RRule.DAILY) {
        setFieldValue(
          withNamespace(
            namespace,
            getFieldName(termType, Suffix.SCHEDULE_SUFFIX),
          ),
          null,
        );
        return;
      }

      const config = { ...termConfig }; // Create a shallow copy
      config.dtstart = null;
      // byweekday can be an array with a null value (when the field is cleared, we should remove null values)
      if (Array.isArray(config.byweekday)) {
        config.byweekday = config.byweekday.filter(day => day !== null);
      }
      if (config.byweekday) {
        config.byweekday = parseWeekdayString(config.byweekday);
      }
      if (config.freq === RRule.YEARLY) {
        const quarterly = getQuarterDayRule(config.bymonthday);
        config.bymonthday = quarterly.bymonthday;
        config.bymonth = quarterly.bymonth;
      }
      // If the frequency is monthly, and we have a byweekday set and it is -1, we need to set it to be the last day of the month
      // This allows the dropdown to show "Last Day of Month" as an option and map it to the RRule correctly.
      if (config.freq === RRule.MONTHLY && config.byweekday === -1) {
        config.bymonthday = -1; // Set to last day of the month
        config.byweekday = undefined; // Clear byweekday since we are using the last day of the month
      }

      const rule = new RRule(config);
      setFieldValue(
        withNamespace(
          namespace,
          getFieldName(termType, Suffix.SCHEDULE_SUFFIX),
        ),
        rule.toString(),
      );
    }
  }, [termConfig, namespace, termType, setFieldValue]);
  // Reset fields when frequency changes, this is so rrule don't end up in a bad state
  // when switching between frequencies that require different fields.
  const prevFreq = useRef(freq);
  useEffect(() => {
    if (prevFreq.current !== freq && freq !== undefined) {
      // Reset each field individually using setFieldValue
      setFieldValue(
        getFieldName(fieldPrefix, Suffix.CONFIG_SUFFIX, FieldName.BY_WEEKDAY),
        undefined,
      );
      setFieldValue(
        getFieldName(fieldPrefix, Suffix.CONFIG_SUFFIX, FieldName.BY_MONTH_DAY),
        undefined,
      );
      setFieldValue(
        getFieldName(fieldPrefix, Suffix.CONFIG_SUFFIX, FieldName.INTERVAL),
        undefined,
      );
      setFieldValue(
        getFieldName(fieldPrefix, Suffix.CONFIG_SUFFIX, FieldName.BY_MONTH),
        undefined,
      );
      setFieldValue(
        getFieldName(fieldPrefix, Suffix.CONFIG_SUFFIX, FieldName.COUNT),
        undefined,
      );

      prevFreq.current = freq;
    }
  }, [freq, fieldPrefix, setFieldValue, termConfig]);

  // Determine which fields to show based on the frequency
  const shouldShow = {
    dateType:
      freq !== undefined && freq !== RRule.WEEKLY && freq !== RRule.DAILY,
    weekday: freq === RRule.WEEKLY || freq === RRule.MONTHLY,
    dayOfMonth: freq === RRule.MONTHLY || freq === RRule.YEARLY,
  };

  return (
    <Column fullWidth>
      <Grid container spacing={2} alignItems="center">
        <Grid item xs={12} md={12}>
          <SelectField
            options={FREQUENCY_OPTIONS}
            name={getFieldName(
              fieldPrefix,
              Suffix.CONFIG_SUFFIX,
              FieldName.FREQ,
            )}
            label="Payment Due Scheduling"
            tooltip="Make payments due at a specific time interval after the payment terms"
          />
        </Grid>
        <Grid item xs={6} md={12}>
          <Input
            type="number"
            name={getFieldName(fieldPrefix, Suffix.TERMS_SUFFIX)}
            label="Payment Terms"
            tooltip="Number of days before payment is due"
          />
        </Grid>

        <Grid item container spacing={2} alignItems="center">
          {shouldShow.dayOfMonth && (
            <Grid
              item
              xs={shouldShow.weekday ? 5 : 12}
              md={shouldShow.weekday ? 5 : 12}
            >
              <Input
                type="number"
                name={getFieldName(
                  fieldPrefix,
                  Suffix.CONFIG_SUFFIX,
                  FieldName.BY_MONTH_DAY,
                )}
                label={
                  freq === RRule.YEARLY ? "Day of Quarter" : "Day of Month"
                }
                tooltip="Choose which day of the period you'd like: enter the day number, like '1' for the first day or '15' for the fifteenth day."
              />
            </Grid>
          )}
          {shouldShow.dayOfMonth && shouldShow.weekday && (
            <Grid item xs={2} md={2}>
              <SmallText justifyContent="space-around">or</SmallText>
            </Grid>
          )}
          {shouldShow.weekday && (
            <Grid
              item
              xs={shouldShow.dayOfMonth ? 5 : 12}
              md={shouldShow.dayOfMonth ? 5 : 12}
            >
              <SelectField
                options={
                  freq === RRule.WEEKLY
                    ? WEEKLY_WEEKDAY_OPTIONS
                    : MONTHLY_WEEKDAY_OPTIONS
                }
                name={`${getFieldName(fieldPrefix, Suffix.CONFIG_SUFFIX, FieldName.BY_WEEKDAY)}`}
                label={freq === RRule.WEEKLY ? "Day of the week" : "Other"}
                isClearable
                tooltip="Choose which day of the week you'd like: you can pick a specific day like 'First Monday' or 'Last Friday'."
              />
            </Grid>
          )}
        </Grid>
        {shouldShow.dateType && (
          <Grid item xs={12} md={12}>
            <SelectField
              options={[
                {
                  value: undefined,
                  label: `First available ${freq === RRule.YEARLY ? "Quarter" : "Month"}`,
                }, // We don't want any value if we are using the current period
                {
                  value: 2,
                  label: `Second available ${freq === RRule.YEARLY ? "Quarter" : "Month"}`,
                }, // If we are using the following period, we want to set the value to 2 so the backend can do the right logic
              ]}
              name={getFieldName(
                fieldPrefix,
                Suffix.CONFIG_SUFFIX,
                FieldName.COUNT,
              )}
              label="In The"
              tooltip="Choose when you'd like this to happen: 'First Month' get the first month based on the options selected, while 'Second Month' moves it to the second month from the options selected."
            />
          </Grid>
        )}
        <Grid item xs={12} md={12}>
          {errors?.[name]?.[index]?.[
            getFieldName(termType, Suffix.CONFIG_SUFFIX)
          ] && (
            <Text color="error">
              {JSON.stringify(
                errors?.[name]?.[index]?.[
                  getFieldName(termType, Suffix.CONFIG_SUFFIX)
                ],
              )}
            </Text>
          )}
        </Grid>
      </Grid>
    </Column>
  );
};
